import type {
    ActiveReservationDetails,
    CheckoutContactInfoValues,
} from '@/types/reservation-checkout'
import type { UpdateReservationContactInfoMutationInput } from '@/types/reservation-checkout'
import type { CountryCode } from 'libphonenumber-js'
import { isSupportedInvoiceCountryCode } from '@/components/checkout/country-options'
import { getNormalizedPhoneValue } from '@/components/checkout/floating-label-phone-input/utils'

export const CONTACT_INFO_FIELDS = [
    'name',
    'email',
    'phone_number',
    'phone_number_2',
    'invoice_title',
    'invoice_street',
    'invoice_city',
    'invoice_zip_code',
    'invoice_country',
    'invoice_ic',
    'invoice_dic',
] as const satisfies ReadonlyArray<keyof CheckoutContactInfoValues>

export const REQUIRED_CONTACT_FIELDS = [
    'name',
    'email',
    'phone_number',
] as const satisfies ReadonlyArray<keyof CheckoutContactInfoValues>

export const INVOICE_FIELDS = [
    'invoice_title',
    'invoice_street',
    'invoice_city',
    'invoice_zip_code',
    'invoice_country',
    'invoice_ic',
    'invoice_dic',
] as const satisfies ReadonlyArray<keyof CheckoutContactInfoValues>

export const OPTIONAL_INVOICE_FIELDS_WHEN_REQUIRED = [
    'invoice_ic',
    'invoice_dic',
] as const satisfies ReadonlyArray<(typeof INVOICE_FIELDS)[number]>

export type CheckoutContactInfoField = (typeof CONTACT_INFO_FIELDS)[number]

function normalizeContactInfoValue(value: string | null | undefined): string {
    return value?.trim() ?? ''
}

export function getReservationContactInfoValues(
    reservation: Pick<ActiveReservationDetails, (typeof CONTACT_INFO_FIELDS)[number] | 'requested_invoice'>,
): CheckoutContactInfoValues {
    const values = {} as CheckoutContactInfoValues

    for (const field of CONTACT_INFO_FIELDS) {
        values[field] = normalizeContactInfoValue(reservation[field])
    }

    return {
        ...values,
        requested_invoice: reservation.requested_invoice,
    }
}

export function hasMissingRequiredContactValues(
    values: CheckoutContactInfoValues,
): boolean {
    return REQUIRED_CONTACT_FIELDS.some(field => values[field].trim() === '')
}

export function hasMissingRequiredInvoiceValues(
    values: Pick<CheckoutContactInfoValues, (typeof INVOICE_FIELDS)[number] | 'requested_invoice'>,
    invoiceRequired: boolean,
): boolean {
    const requiredInvoiceFields = invoiceRequired
        ? INVOICE_FIELDS.filter(field => !OPTIONAL_INVOICE_FIELDS_WHEN_REQUIRED.includes(
            field as (typeof OPTIONAL_INVOICE_FIELDS_WHEN_REQUIRED)[number],
        ))
        : INVOICE_FIELDS

    return values.requested_invoice
        && (
            requiredInvoiceFields.some(field => values[field].trim() === '')
            || !isSupportedInvoiceCountryCode(values.invoice_country)
        )
}

export function isContactInfoEmpty(
    values: Pick<CheckoutContactInfoValues, (typeof CONTACT_INFO_FIELDS)[number]>,
): boolean {
    return CONTACT_INFO_FIELDS.every(field => values[field].trim() === '')
}

export function isValidContactEmail(value: string): boolean {
    return /\S+@\S+\.\S+/.test(value.trim())
}

export function normalizeRequiredContactInfoValue(value: string): string {
    return value.trim()
}

export function normalizeOptionalContactInfoValue(value: string): string | null {
    const trimmedValue = value.trim()
    return trimmedValue === '' ? null : trimmedValue
}

export function buildContactInfoPayload({
    values,
    showPhoneNumber2,
    defaultPhoneCountry,
}: {
    values: CheckoutContactInfoValues
    showPhoneNumber2: boolean
    defaultPhoneCountry: CountryCode
}): UpdateReservationContactInfoMutationInput {
    const payload: UpdateReservationContactInfoMutationInput = {
        name: normalizeRequiredContactInfoValue(values.name),
        email: normalizeRequiredContactInfoValue(values.email),
        phone_number: getNormalizedPhoneValue(values.phone_number, defaultPhoneCountry),
        requested_invoice: values.requested_invoice,
        phone_number_2: showPhoneNumber2
            ? normalizeOptionalContactInfoValue(
                getNormalizedPhoneValue(values.phone_number_2, defaultPhoneCountry),
            )
            : null,
    }

    if (values.requested_invoice) {
        payload.invoice_title = normalizeRequiredContactInfoValue(values.invoice_title)
        payload.invoice_street = normalizeRequiredContactInfoValue(values.invoice_street)
        payload.invoice_city = normalizeRequiredContactInfoValue(values.invoice_city)
        payload.invoice_zip_code = normalizeRequiredContactInfoValue(values.invoice_zip_code)
        payload.invoice_country = normalizeRequiredContactInfoValue(values.invoice_country)
        payload.invoice_ic = normalizeRequiredContactInfoValue(values.invoice_ic)
        payload.invoice_dic = normalizeRequiredContactInfoValue(values.invoice_dic)
    } else {
        payload.invoice_title = null
        payload.invoice_street = null
        payload.invoice_city = null
        payload.invoice_zip_code = null
        payload.invoice_country = null
        payload.invoice_ic = null
        payload.invoice_dic = null
    }

    return payload
}
